import { useMemo, useRef, useState } from "react";
import { Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  RevenueIntelligenceChatChartVisual,
} from "./RevenueIntelligenceChatChart";
import type {
  RevenueIntelligenceChatDataset,
} from "./RevenueIntelligenceChatContext";
import type {
  RevenueIntelligenceSemanticPlan,
} from "../lib/revenue-intelligence-semantic-planner";
import {
  exportRevenueIntelligenceBrief,
  getRevenueIntelligenceExportPeriodLabel,
} from "../lib/revenue-intelligence-chart-export";

export type RevenueIntelligenceBriefItem = {
  conclusion: string | null;
  dataset: RevenueIntelligenceChatDataset;
  plan: RevenueIntelligenceSemanticPlan;
};

export function RevenueIntelligenceChatBrief({
  items,
  language = "en",
  onPublished,
  period,
  title,
}: {
  items: RevenueIntelligenceBriefItem[];
  language?: "en" | "sv";
  onPublished: () => Promise<void> | void;
  period?: string | null;
  title: string;
}) {
  const briefRef = useRef<HTMLDivElement>(null);
  const [exporting, setExporting] = useState(false);
  const dataAsOf = useMemo(
    () => new Intl.DateTimeFormat(language === "sv" ? "sv-SE" : "en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }).format(new Date()),
    [language],
  );
  const selectedItems = items.slice(0, 3);
  const periodLabel = getRevenueIntelligenceExportPeriodLabel(period, language);
  const dataAsOfLabel = language === "sv" ? `Data per ${dataAsOf}` : `Data as of ${dataAsOf}`;

  const exportBrief = async () => {
    const chartElements = Array.from(
      briefRef.current?.querySelectorAll<HTMLElement>("[data-brief-chart]")
      ?? [],
    );
    const charts = chartElements.flatMap((element) => {
      const chart = element.querySelector("svg");
      if (!chart) return [];
      return [{
        chart,
        conclusion: element.dataset.conclusion || null,
        source: element.dataset.source ?? "Revenue Intelligence",
        title: element.dataset.title ?? "Analysis",
      }];
    });
    setExporting(true);
    try {
      await exportRevenueIntelligenceBrief({
        charts,
        dataAsOf,
        period,
        title,
        language,
      });
      void Promise.resolve(onPublished()).catch(() => undefined);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="mt-3 overflow-hidden rounded-[10px] border border-[#5E548E]/20 bg-white">
      <div
        ref={briefRef}
        className="aspect-video min-h-64 bg-gray-50 p-3"
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-sm font-medium text-[#231942]">{title}</p>
            <p className="mt-0.5 text-[10px] text-gray-500">
              {[periodLabel, dataAsOfLabel].filter(Boolean).join(" · ")}
            </p>
          </div>
        </div>
        <div
          className={selectedItems.length === 1
            ? "mt-3 grid grid-cols-1"
            : selectedItems.length === 2
              ? "mt-3 grid grid-cols-2 gap-2"
              : "mt-3 grid grid-cols-3 gap-2"}
        >
          {selectedItems.map((item) => {
            const chart = item.plan.chart;
            if (!chart) return null;
            return (
              <div
                key={item.dataset.datasetId}
                data-brief-chart
                data-title={chart.title}
                data-source={item.dataset.label}
                data-conclusion={item.conclusion ?? ""}
                className="min-w-0 rounded-lg border border-gray-200 bg-white p-2"
              >
                <p className="truncate text-[10px] font-medium text-[#231942]">
                  {chart.title}
                </p>
                <RevenueIntelligenceChatChartVisual
                  chart={chart}
                  className="h-40 pt-2"
                  dataset={item.dataset}
                  language={language}
                />
              </div>
            );
          })}
        </div>
        <div className="mt-2 flex items-center justify-end gap-1.5 text-[9px] text-[#5E548E]">
          <img src="/revelate-logo.svg" alt="Revelate" className="h-3 w-auto" />
          <span>Intelligence by Revelate</span>
        </div>
      </div>
      <div className="flex items-center gap-2 border-t border-gray-100 px-3 py-2">
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="h-7 gap-1.5 px-2 text-[10px]"
          disabled={exporting}
          onClick={() => void exportBrief()}
        >
          <Download className="h-3 w-3" />
          {exporting
            ? language === "sv" ? "Förbereder…" : "Preparing…"
            : language === "sv" ? "Ladda ned 16:9 PNG" : "Download 16:9 PNG"}
        </Button>
      </div>
    </div>
  );
}
